import { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const DESIG_RANK = {"Dean":0,"Professor-HOD":1,"Professor - HOD":1,"Professor":2,"Associate Professor":3,"Assistant Professor":4,"Senior Resident":5,"Junior Resident":6,"Demonstrator":7,"Tutor":8};
const DESIG_COLS = new Set(["designation_fims","designation_aebas"]);

export default function AebasDiscrepancies() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc"); // "asc" | "desc"

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get("/aebas/discrepancies");
        setData(d);
      } catch {}
      setLoading(false);
    })();
  }, []);

  const isAdmin = user?.role === "admin" || user?.role === "confidential";

  const rows = data?.discrepancies || [];
  const synced = rows.filter(r => r.status === "synced");
  const mismatches = synced.filter(r => !r.designation_match || !r.department_match);
  const matching = synced.filter(r => r.designation_match && r.department_match);
  const noAebasId = rows.filter(r => r.status === "no_aebas_id");
  const notMatched = rows.filter(r => r.status === "not_matched");
  const aebasOnly = rows.filter(r => r.status === "aebas_only");
  const nmcRemoved = rows.filter(r => r.status === "nmc_removed");

  const categoryFiltered = filter === "all" ? rows
    : filter === "mismatch" ? mismatches
    : filter === "match" ? matching
    : filter === "no_id" ? noAebasId
    : filter === "not_matched" ? notMatched
    : filter === "aebas_only" ? aebasOnly
    : filter === "nmc_removed" ? nmcRemoved
    : rows;

  // Search filter
  const searchFiltered = useMemo(() => {
    if (!search.trim()) return categoryFiltered;
    const q = search.trim().toLowerCase();
    return categoryFiltered.filter(r =>
      (r.full_name || "").toLowerCase().includes(q) ||
      (r.aebas_id || "").toLowerCase().includes(q)
    );
  }, [categoryFiltered, search]);

  // Sort
  const filtered = useMemo(() => {
    if (!sortCol) return searchFiltered;
    const arr = [...searchFiltered];
    arr.sort((a, b) => {
      let av, bv;
      if (DESIG_COLS.has(sortCol)) {
        av = DESIG_RANK[a[sortCol]] ?? 99;
        bv = DESIG_RANK[b[sortCol]] ?? 99;
      } else {
        av = (a[sortCol] || "").toString().toLowerCase();
        bv = (b[sortCol] || "").toString().toLowerCase();
      }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [searchFiltered, sortCol, sortDir]);

  if (!isAdmin) return <div style={{padding:40,textAlign:'center',color:'var(--text-3)'}}>Admin access required.</div>;

  const toggleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  };

  const statusLabel = (s) => s === "synced" ? null : s === "no_aebas_id" ? "No AEBAS ID" : s === "not_matched" ? "AEBAS ID Not Matched" : s === "aebas_only" ? "In AEBAS Only" : s === "nmc_removed" ? "Removed from NMC Database" : null;
  const statusColor = (s) => s === "no_aebas_id" ? "#ca8a04" : s === "not_matched" ? "#6366f1" : s === "aebas_only" ? "#0891b2" : s === "nmc_removed" ? "#9333ea" : null;

  const thStyle = {padding:'8px 10px',textAlign:'left',whiteSpace:'nowrap',cursor:'pointer',userSelect:'none'};

  const COLUMNS = [
    {key:'full_name', label:'Faculty'},
    {key:'ssmch_id', label:'SSMCH ID'},
    {key:'aebas_id', label:'AEBAS ID'},
    {key:'designation_fims', label:'Designation (FIMS)'},
    {key:'designation_aebas', label:'Designation (AEBAS)'},
    {key:'_desig_match', label:'\u21C4', sortable:false, center:true, width:30},
    {key:'department_fims', label:'Department (FIMS)'},
    {key:'department_aebas', label:'Department (AEBAS)'},
    {key:'_dept_match', label:'\u21C4', sortable:false, center:true, width:30},
    {key:'status', label:'Status'},
  ];

  return (
    <div data-testid="view-aebas-discrepancies">
      <div className="topbar">
        <div>
          <h1 className="pg-title">AEBAS Discrepancies</h1>
          <div className="pg-sub">Compare NMC Portal (AEBAS) data against FIMS records</div>
        </div>
        <div className="topbar-meta" style={{display:'flex',gap:8,alignItems:'center'}}>
          {data && <span className="meta-chip">{data.total} faculty · {data.synced} synced</span>}
          <button className="btn" onClick={() => { setLoading(true); setData(null); (async () => { try { const { data: d } = await api.get("/aebas/discrepancies"); setData(d); } catch {} setLoading(false); })(); }}
            style={{fontSize:12,padding:'6px 16px'}} data-testid="refresh-discrepancies">
            Refresh Discrepancies
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{padding:40,textAlign:'center'}}><div className="spinner"/></div>
      ) : rows.length === 0 ? (
        <div style={{padding:'60px 20px',textAlign:'center'}}>
          <div style={{fontSize:16,color:'var(--text-2)',fontFamily:'var(--serif)'}}>No faculty data</div>
        </div>
      ) : (
        <>
          {/* Search bar */}
          <div style={{marginBottom:14}}>
            <input
              className="form-input"
              placeholder="Search by name or AEBAS ID..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{maxWidth:380,fontSize:13,padding:'8px 14px'}}
              data-testid="discrepancy-search"
            />
          </div>

          {/* Filter tabs */}
          <div style={{display:'flex',gap:8,marginBottom:16,flexWrap:'wrap'}}>
            {[
              {key:'all', label:`All (${rows.length})`},
              {key:'mismatch', label:`Mismatches (${mismatches.length})`, color: mismatches.length > 0 ? '#dc2626' : undefined},
              {key:'match', label:`Matching (${matching.length})`},
              {key:'no_id', label:`No AEBAS ID (${noAebasId.length})`, color: noAebasId.length > 0 ? '#ca8a04' : undefined},
              {key:'not_matched', label:`Not Matched (${notMatched.length})`, color: notMatched.length > 0 ? '#6366f1' : undefined},
              {key:'aebas_only', label:`In AEBAS Only (${aebasOnly.length})`, color: aebasOnly.length > 0 ? '#0891b2' : undefined},
              {key:'nmc_removed', label:`Removed from NMC Database (${nmcRemoved.length})`, color: nmcRemoved.length > 0 ? '#9333ea' : undefined},
            ].map(f => (
              <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filter-${f.key}`}
                style={{padding:'6px 16px',fontSize:12,fontWeight:filter===f.key?600:400,border:'1px solid',
                  borderColor:filter===f.key?'var(--text-1)':'var(--border)',borderRadius:6,
                  background:filter===f.key?'var(--text-1)':'transparent',
                  color:filter===f.key?'#fff':(f.color||'var(--text-2)'),cursor:'pointer'}}>
                {f.label}
              </button>
            ))}
          </div>

          {/* Result count when searching */}
          {search.trim() && (
            <div style={{fontSize:12,color:'var(--text-3)',marginBottom:8}} data-testid="search-result-count">
              {filtered.length} result{filtered.length !== 1 ? 's' : ''} for "{search.trim()}"
            </div>
          )}

          {/* Export + count bar */}
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
            <div style={{fontSize:12,color:'var(--text-3)'}}>
              Showing <strong>{filtered.length}</strong> of {rows.length} records
            </div>
            <button className="btn" onClick={() => {
              const getStatus = (r) => {
                if (r.status === "synced") return r.designation_match && r.department_match ? "Matched" : "Mismatch";
                return statusLabel(r.status) || r.status;
              };
              const header = ["Faculty","SSMCH ID","AEBAS ID","Designation (FIMS)","Designation (AEBAS)","Designation Match","Department (FIMS)","Department (AEBAS)","Department Match","Status"];
              const csvRows = filtered.map(r => [
                r.full_name || "",
                r.ssmch_id || "",
                r.aebas_id ? `="${r.aebas_id}"` : "",
                r.designation_fims || "",
                r.designation_aebas || "",
                r.status === "synced" ? (r.designation_match ? "Yes" : "No") : "",
                r.department_fims || "",
                r.department_aebas || "",
                r.status === "synced" ? (r.department_match ? "Yes" : "No") : "",
                getStatus(r),
              ]);
              const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
              const csv = "\uFEFF" + [header.map(esc).join(","), ...csvRows.map(r => r.map(esc).join(","))].join("\n");
              const blob = new Blob([csv], {type:"text/csv;charset=utf-8-sig"});
              const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
              const tabName = filter === "all" ? "All" : filter === "mismatch" ? "Mismatches" : filter === "match" ? "Matching" : filter === "no_id" ? "No_AEBAS_ID" : filter === "not_matched" ? "Not_Matched" : filter === "aebas_only" ? "AEBAS_Only" : filter === "nmc_removed" ? "NMC_Removed" : "Export";
              a.download = `AEBAS_Discrepancies_${tabName}.csv`; a.click(); URL.revokeObjectURL(a.href);
            }} style={{fontSize:12,padding:'6px 16px'}} data-testid="export-discrepancies">
              Export CSV ({filtered.length})
            </button>
          </div>

          <div style={{overflowX:'auto',border:'1px solid var(--border)',borderRadius:8}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}} data-testid="discrepancy-table">
              <thead>
                <tr style={{background:'var(--bg-alt)',borderBottom:'1px solid var(--border)'}}>
                  {COLUMNS.map(col => (
                    <th key={col.key}
                      onClick={col.sortable !== false ? () => toggleSort(col.key) : undefined}
                      style={{...thStyle, textAlign: col.center ? 'center' : 'left', width: col.width || 'auto', cursor: col.sortable !== false ? 'pointer' : 'default'}}
                      data-testid={`th-${col.key}`}
                      dangerouslySetInnerHTML={col.sortable !== false
                        ? {__html: `${col.label}${sortCol === col.key ? (sortDir === 'asc' ? ' &#9650;' : ' &#9660;') : ' <span style="opacity:0.25;font-size:10px">&#8597;</span>'}`}
                        : {__html: col.label}
                      }
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, idx) => {
                  const isSynced = r.status === "synced";
                  const desigOk = r.designation_match;
                  const deptOk = r.department_match;
                  const bgColor = !isSynced ? (r.status === "no_aebas_id" ? 'rgba(234,179,8,0.04)' : r.status === "aebas_only" ? 'rgba(8,145,178,0.04)' : r.status === "nmc_removed" ? 'rgba(147,51,234,0.04)' : 'rgba(99,102,241,0.04)')
                    : (!desigOk || !deptOk) ? 'rgba(248,113,113,0.04)' : 'transparent';
                  const rowKey = r.faculty_id || `${r.status}-${r.aebas_id || idx}`;
                  return (
                    <tr key={rowKey} style={{borderBottom:'1px solid var(--line-soft)', background: bgColor}} data-testid={`row-${rowKey}`}>
                      <td style={{padding:'7px 10px',fontWeight:500}}>{r.full_name}</td>
                      <td style={{padding:'7px 10px',fontFamily:'var(--mono)',fontSize:11}}>{r.ssmch_id}</td>
                      <td style={{padding:'7px 10px',fontFamily:'var(--mono)',fontSize:11}}>{r.aebas_id || <span style={{color:'var(--text-3)'}}>—</span>}</td>
                      <td style={{padding:'7px 10px'}}>{r.designation_fims || '—'}</td>
                      <td style={{padding:'7px 10px', color: isSynced ? (desigOk ? 'var(--text-2)' : '#dc2626') : 'var(--text-3)', fontWeight: isSynced && !desigOk ? 600 : 400}}>
                        {r.designation_aebas || (isSynced ? '—' : '')}
                      </td>
                      <td style={{padding:'7px 10px',textAlign:'center'}}>
                        {isSynced ? (desigOk ? <span style={{color:'#059669'}}>&#10003;</span> : <span style={{color:'#dc2626'}}>&#10007;</span>) : ''}
                      </td>
                      <td style={{padding:'7px 10px'}}>{r.department_fims || '—'}</td>
                      <td style={{padding:'7px 10px', color: isSynced ? (deptOk ? 'var(--text-2)' : '#dc2626') : 'var(--text-3)', fontWeight: isSynced && !deptOk ? 600 : 400}}>
                        {r.department_aebas || (isSynced ? '—' : '')}
                      </td>
                      <td style={{padding:'7px 10px',textAlign:'center'}}>
                        {isSynced ? (deptOk ? <span style={{color:'#059669'}}>&#10003;</span> : <span style={{color:'#dc2626'}}>&#10007;</span>) : ''}
                      </td>
                      <td style={{padding:'7px 10px',whiteSpace:'nowrap'}}>
                        {isSynced ? (
                          desigOk && deptOk ? <span style={{color:'#059669',fontSize:11}}>Matched</span> : <span style={{color:'#dc2626',fontSize:11,fontWeight:600}}>Mismatch</span>
                        ) : (
                          <span style={{fontSize:11,fontWeight:500,color:statusColor(r.status)}}>{statusLabel(r.status)}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
